import { z } from 'zod';
import { FieldConfigurationSchema } from './custom-field.dto';
import { CustomFieldValueSchema } from './custom-field-value.dto';
import { inferApiResultSchema, inferPaginatedApiResponse } from './infer-api';
import { LifecycleFilterSchema } from './types.dto';

export const PriorityFrameworkCategory = {
  GARTNER: 'GARTNER',
  SAFE: 'SAFE',
  KANO: 'KANO',
} as const;

export type PriorityFrameworkCategoryType =
  (typeof PriorityFrameworkCategory)[keyof typeof PriorityFrameworkCategory];

export const frameworkNameLimit = 300;
export const frameworkDescriptionLimit = 3000;
export const frameworkLearnMoreUrlLimit = 2000;

export const PriorityFrameworkFieldSchema = z
  .object({
    name: z.string().trim().min(1).max(frameworkNameLimit),
    description: z.string().trim().min(1).max(frameworkDescriptionLimit),
    required: z.boolean().default(false),
  })
  .and(FieldConfigurationSchema);

export type PriorityFrameworkFieldDto = z.infer<typeof PriorityFrameworkFieldSchema>;

export const PriorityFrameworkSchema = z.object({
  id: z.number(),
  frameworkName: z.string().trim().min(1).max(frameworkNameLimit),
  category: z.string(),
  description: z.string().max(frameworkDescriptionLimit).nullish(),
  learnMoreUrl: z.string().max(frameworkLearnMoreUrlLimit).nullish(),
  updatedAt: z.date().nullish(),
  createdAt: z.date().nullish(),
  deletedAt: z.date().nullish(),
  archivedAt: z.date().nullish(),
  fields: z.array(PriorityFrameworkFieldSchema),
  enabled: z.boolean().optional(),
});

export type PriorityFrameworkDto = z.infer<typeof PriorityFrameworkSchema>;

export const PriorityFrameworkNoIdSchema = PriorityFrameworkSchema.omit({
  id: true,
});
export type PriorityFrameworkNoIdDto = z.infer<typeof PriorityFrameworkNoIdSchema>;

export const CreatePriorityFrameworkSchema = z.object({
  frameworkName: z.string().trim().min(1).max(frameworkNameLimit),
  category: z.string(),
  description: z.string().max(frameworkDescriptionLimit).optional(),
  learnMoreUrl: z.string().max(frameworkLearnMoreUrlLimit).optional(),
  fields: z.array(CustomFieldValueSchema).optional().default([]),
});
export type CreatePriorityFrameworkDto = z.infer<typeof CreatePriorityFrameworkSchema>;

export const UpdatePriorityFrameworkSchema = CreatePriorityFrameworkSchema.partial().extend({
  id: z.number(),
});
export type UpdatePriorityFrameworkDto = z.infer<typeof UpdatePriorityFrameworkSchema>;

// API Response schemas
export const GetPriorityFrameworkResponseSchema = inferApiResultSchema(PriorityFrameworkSchema);
export type GetPriorityFrameworkResponseDto = z.infer<typeof GetPriorityFrameworkResponseSchema>;

export const GetPriorityFrameworksResponseSchema = inferApiResultSchema(
  z.array(PriorityFrameworkSchema),
);
export type GetPriorityFrameworksResponseDto = z.infer<typeof GetPriorityFrameworksResponseSchema>;

export const GetPaginatedPriorityFrameworksResponseSchema = inferPaginatedApiResponse(
  z.array(PriorityFrameworkSchema),
);
export type GetPaginatedPriorityFrameworksResponseDto = z.infer<
  typeof GetPaginatedPriorityFrameworksResponseSchema
>;

export const CreatePriorityFrameworkResponseSchema = inferApiResultSchema(PriorityFrameworkSchema);
export type CreatePriorityFrameworkResponseDto = z.infer<
  typeof CreatePriorityFrameworkResponseSchema
>;

export const UpdatePriorityFrameworkResponseSchema = inferApiResultSchema(PriorityFrameworkSchema);
export type UpdatePriorityFrameworkResponseDto = z.infer<
  typeof UpdatePriorityFrameworkResponseSchema
>;

// Query parameters for filtering/sorting
export const PriorityFrameworkQuerySchema = z.object({
  category: z.string().optional(),
  search: z.string().optional(),
  includeArchived: z.boolean().optional().default(false),
  includeDeleted: z.boolean().optional().default(false),
  includeDisabledFrameworks: z.boolean().optional().default(false),
  lifecycleFilter: LifecycleFilterSchema.optional(),
  offset: z.number().nonnegative().optional().default(0),
  limit: z.number().nonnegative().max(100).optional().default(25),
  sort: z
    .object({
      column: z
        .enum(['frameworkName', 'category', 'createdAt', 'updatedAt'])
        .optional()
        .default('frameworkName'),
      order: z.enum(['asc', 'desc']).optional().default('asc'),
    })
    .optional(),
});
export type PriorityFrameworkQueryDto = z.infer<typeof PriorityFrameworkQuerySchema>;

export const CloneCustomFieldsRequestSchema = z.object({
  fieldNameMapping: z.record(z.string(), z.string().trim().min(1).max(frameworkNameLimit)),
});
export type CloneCustomFieldsRequestDto = z.infer<typeof CloneCustomFieldsRequestSchema>;
